import { useEffect, useRef } from 'react';
import { CLIP, VIDEO_TYPE } from '../../enums';
import { useClipFavourites } from './context';

// A lock is an entry trigger. Manual angle changes inside the clip stay untouched.
export const useClipAngleLock = ({ feedId, clip, videos, angleIndex, ready, switchAngle }: {
  feedId: string | undefined;
  clip?: CLIP;
  videos: VIDEO_TYPE[];
  angleIndex: number;
  ready: boolean;
  switchAngle: (index: number) => void;
}) => {
  const { clipEntry } = useClipFavourites();
  const state = useRef<{
    feedId: string | undefined;
    clipKey?: string;
    previousAngle?: { id: string; index: number };
  }>({ feedId });

  useEffect(() => {
    if (state.current.feedId !== feedId) state.current = { feedId };
    if (!ready) return;
    if (!clip) {
      state.current.clipKey = undefined;
      return;
    }

    const clipKey = `${clip.id}:${clipEntry?.id === String(clip.id) ? clipEntry.revision : ''}`;
    if (state.current.clipKey === clipKey) {
      // Unlocking the current clip ends its temporary angle override.
      if (clip.locked_angle_id == null) state.current.previousAngle = undefined;
      return;
    }
    state.current.clipKey = clipKey;

    const lockedIndex = clip.locked_angle_id == null ? -1
      : videos.findIndex((video) => video && String(video.id) === String(clip.locked_angle_id));
    if (lockedIndex >= 0) {
      if (!state.current.previousAngle && videos[angleIndex]) {
        state.current.previousAngle = { id: String(videos[angleIndex].id), index: angleIndex };
      }
      switchAngle(lockedIndex);
      return;
    }

    const previousAngle = state.current.previousAngle;
    if (!previousAngle) return;
    state.current.previousAngle = undefined;
    const previousIndex = videos.findIndex((video) => video && String(video.id) === previousAngle.id);
    // Composer clips can come from different collections; keep the same angle position there.
    const index = previousIndex >= 0 ? previousIndex : previousAngle.index;
    if (videos[index]) switchAngle(index);
  }, [feedId, clip, clipEntry, videos, angleIndex, ready, switchAngle]);
};
